import type {
    GridPaymentGridData,
    PaymentGridBuild,
    PaymentGridCarriedOverride,
    PaymentGridOverriddenInputIndices,
    PaymentGridRestoredInputs
} from '../types/paymentTypes';
import { roundWithPrecision } from '@makemydeal/dr-common-utils';
import type {
    GenericObject,
    IPencilOfferInputs,
    OfferType,
    PaymentGridCellItem,
    PaymentGridRowStatus,
    PaymentSchedule,
    PaymentGridCellPayment,
    PDfPaymentGrid,
    PdfTermGridItem,
    ResidualValueType,
    SellRateItem,
    SpecialPaymentFormulaType,
    TermMonthItem
} from '@makemydeal/dr-platform-types';
import {
    BALLOON,
    CASH,
    FINANCE,
    LEASE,
    RESIDUAL_DOLLAR,
    RESIDUAL_PERCENT,
    SpecialPaymentFormula
} from '@makemydeal/dr-platform-types';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';
import { createSelector } from 'reselect';
import type { AdjustedResidualItem, ResidualItem, ResidualsFromTerms, SellRatesFromTerms } from '../types/commonTypes';
import { DOWN_PAYMENT_DIFFERENCE, getGridTerms } from '../utils/paymentGridUtils';
import {
    getFlexBuyPaymentGridCashDowns,
    getFlexBuyPaymentGridItems,
    getFlexBuyPaymentGridPercents,
    getFlexBuyPaymentGridTerms
} from './flexBuyPaymentsSelector';
import { hasUndefinedValue } from './helpers';
import {
    getCurrentOfferType,
    getDownPayment,
    getDownPaymentByOfferType,
    getFeatureToggle,
    getIsUserProgramQuotes,
    getOfferDetails,
    getPaymentGridToggle,
    getSelectedTerm,
    getSelectedTermByOfferType,
    getTerm,
    getTermByOfferTypeAndMonths,
    isFlexBuyPayment,
    isUpqResidualKeepOnMileageChangeEnabled
} from './offerNodeSelectors';
import { getAutoTermId } from './offerSelectors';
import {
    getPaymentProgramInfoByTerm,
    getResidualInfoOverride,
    getResidualInfoOverrideByOfferTypeTerm,
    isUpqLockKeptTerm
} from './paymentSelectors';
import {
    getProgramResidualOverrideForSelectedTerm,
    getRateTypeByTerm,
    getSelectedTermMonths,
    getSellRateByOfferType,
    getSellRateOverrideByOfferTypeAndTerm,
    getSpecialPaymentFormula
} from './selectedTermSelectors';
import { getTermId, isOnePayLeaseFrequency } from './utils/nonSelectorUtils';
import { isCashEquityAllocationMethod } from '../utils/equityAllocationUtils';
import { isEmpty } from '../utils/common';
import {
    PaymentGridSource,
    appliesToTerm,
    hasTerm,
    isGridComplete,
    isUserProgramQuotesColumn,
    keepsColumnResidualOnMileageChange,
    keepsTermResidualOnMileageChange,
    resolveByColumn,
    resolveCellVerdict,
    resolveDealColumnIndex,
    resolveTermOwnerColumnIndex
} from '../utils/paymentGridSourceUtils';
import type { ColumnPairFacts } from '../utils/leaseGridColumnUtils';
import { getQuotedTerms, isColumnAwaitingPair, isColumnPairRejected } from '../utils/leaseGridColumnUtils';
import { isUserPricedProgram, isValidProgram } from '../utils/lenderProgramUtils';

import {
    ASP_PG_REDESIGN_TOGGLE,
    EXPLICIT_LENDER_P1_TOGGLE,
    PAYMENT_GRID_BAGGAGE_COMPRESSION_TOGGLE,
    PAYMENT_GRID_PERSISTENCE_TOGGLE
} from '../constants/meta';
import { RATE_TYPE } from '../constants/payment';
import { isManualRebatePreservationEnabled } from '../utils/incentives';

/**
 * The single gate for the redesign in this package. **Deal-type scope lives here and nowhere else.** Finance and Lease
 * ship; Balloon and Cash never populate `columnSources`, so every column would read as undecided and the render gate
 * would never open. To extend it, edit this and dash-components' `useIsAspPaymentGridRedesignActive`.
 */
export const isAspPaymentGridRedesignActive = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    if (!getFeatureToggle(state, ASP_PG_REDESIGN_TOGGLE)) return false;

    const currentOfferType = offerType || getCurrentOfferType(state);
    if (currentOfferType !== FINANCE && currentOfferType !== LEASE) return false;

    return !isFlexBuyPayment(state);
};

export const isPaymentGridPersistenceActive = (state: OfferReduxBaseState, offerType?: OfferType): boolean =>
    getFeatureToggle(state, PAYMENT_GRID_PERSISTENCE_TOGGLE) && isAspPaymentGridRedesignActive(state, offerType);

export const isManualRebatePreservationActive = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    if (!isManualRebatePreservationEnabled(state)) return false;

    const currentOfferType = offerType || getCurrentOfferType(state);
    if (currentOfferType !== FINANCE && currentOfferType !== LEASE) return false;

    return !isFlexBuyPayment(state);
};

export const isExplicitLenderProgramGridActive = (state: OfferReduxBaseState, offerType?: OfferType): boolean =>
    getFeatureToggle(state, EXPLICIT_LENDER_P1_TOGGLE) && isAspPaymentGridRedesignActive(state, offerType);

export const getDealLenderProgram = (state: OfferReduxBaseState, offerType?: OfferType): any => {
    const currentOfferType = offerType || getCurrentOfferType(state);

    const selectedTerm = getSelectedTermByOfferType(state, currentOfferType);
    const dealProgram = selectedTerm?.lenderProgram;
    if (isUserPricedProgram(dealProgram)) return undefined;
    if (
        isExplicitLenderProgramGridActive(state, currentOfferType) &&
        isUpqLockKeptTerm(state, currentOfferType, selectedTerm?.months)
    ) {
        return undefined;
    }
    if (isValidProgram(dealProgram)) return dealProgram;

    if (selectedTerm?.months != null && selectedTerm.months === getPaymentGridCalculatorPricedDealTerm(state, currentOfferType)) {
        return undefined;
    }

    // A term edit on the deal's own column moves the selected term before it is priced, so for that edit the record
    // above describes a term with nothing on it yet. The program the deal opened the grid on still holds (DE774309).
    const dealProgramAtOpen = getPaymentGridDealProgramAtOpen(state, currentOfferType);
    if (isValidProgram(dealProgramAtOpen)) return dealProgramAtOpen;

    const cached = getPaymentProgramInfoByTerm(state, currentOfferType);
    if (isUserPricedProgram(cached)) return undefined;

    // Every Lease grid row call carries `skipProgramInfoUpdate`, so while a build is in flight the cache is not being
    // rewritten by the responses the terms reducer is merging - with `enableProgramIncentivesRestoration_F292564` off
    // a term whose program the response declined loses it while the cache keeps the old one. The deal's own selected
    // term is the only live statement of the program on the deal for the length of a grid session.
    if (isExplicitLenderProgramGridActive(state, currentOfferType) && getPaymentGridBuild(state, currentOfferType)) {
        return undefined;
    }

    return cached;
};

export const hasDealLenderProgram = (state: OfferReduxBaseState, offerType?: OfferType): boolean =>
    isValidProgram(getDealLenderProgram(state, offerType));

export const getPaymentGridTerms = (state: OfferReduxBaseState, offerType?: OfferType): (number | null)[] => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    const isFlexBuy = isFlexBuyPayment(state);

    return (isFlexBuy ? getFlexBuyPaymentGridTerms(state) : offerDetails.paymentGridData?.terms) || [];
};

export function getPaymentGridErrorState(state: OfferReduxBaseState): {
    lastUpdatedInput: string | undefined;
    hasError: boolean | undefined;
} {
    const currentOfferType = getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    const isRoundStillOut =
        isAspPaymentGridRedesignActive(state, currentOfferType) &&
        Object.values(offerDetails.paymentGridData?.rowsStatuses ?? {}).some((rowStatus) => rowStatus?.isLoading);
    return {
        lastUpdatedInput: offerDetails.paymentGridData?.lastUpdatedInput,
        hasError: isRoundStillOut ? false : offerDetails.paymentGridData?.hasError
    };
}

export const getUniquePaymentGridTerms = (state: any, offerType?: OfferType) =>
    [...new Set(getPaymentGridTerms(state, offerType))].filter(hasTerm);

export const getPaymentGridCashDown = (state: any, offerType?: OfferType) => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);

    const isFlexBuy = isFlexBuyPayment(state);

    return (isFlexBuy ? getFlexBuyPaymentGridCashDowns(state) : offerDetails.paymentGridData?.cashDown) || [];
};

export const getUniquePaymentGridCashDown = (state: OfferReduxBaseState, offerType?: OfferType) =>
    [...new Set(getPaymentGridCashDown(state, offerType))].filter((cash) => cash !== null);

/**
 * The MV-priced grid cell payments carried on the baggage, stashed onto paymentGridData when the grid is
 * enabled. Indexed [downPaymentIndex][termIndex] to match the grid axes. Lets Sales View render the
 * non-active cells (which are never hydrated into terms.byId) straight from the saved grid.
 */
export const getPaymentGridCellPayments = (state: any, offerType?: OfferType): PaymentGridCellPayment[][] | undefined => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    return offerDetails.paymentGridData?.gridCellPayments;
};

const EMPTY_ROWS_STATUSES: Record<number, PaymentGridRowStatus> = {};

/**
 * US2041815: when each grid cell last priced successfully, keyed by term id. Absent for a cell that has
 * not priced in this session and whose stored timestamp has not been restored.
 */
const EMPTY_CELL_PRICED_AT: Record<string, number> = {};

export const getPaymentGridCellPricedAt = (state: OfferReduxBaseState, offerType?: OfferType): Record<string, number> => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    // A shared constant, as `rowsStatuses` and `columnSources` do: a fresh `{}` here re-rendered every
    // subscribed grid row on every action, because `useSelector` compares by reference.
    return (offerDetails.paymentGridData as GridPaymentGridData)?.cellPricedAt || EMPTY_CELL_PRICED_AT;
};

export const isPaymentGridCellLoading = (
    state: OfferReduxBaseState,
    offerType: OfferType | undefined,
    columnIndex: number,
    rowIndex: number,
    termMonths: number | null
): boolean => {
    const rowsStatuses = getRowsStatuses(state, offerType) as
        | Record<number, { isLoading?: boolean; terms?: (number | null)[]; loadingColumns?: number[]; requestedColumns?: number[] }>
        | undefined;
    const rowStatus = rowsStatuses?.[rowIndex];
    return resolveByColumn(
        Boolean(rowStatus?.requestedColumns?.length),
        () => Boolean(rowStatus?.loadingColumns?.includes(columnIndex)),
        () => (rowStatus?.isLoading && rowStatus?.terms?.includes(termMonths)) ?? false
    );
};

export const getRowsStatuses = (state: OfferReduxBaseState, offerType?: OfferType) => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    return offerDetails.paymentGridData?.rowsStatuses || EMPTY_ROWS_STATUSES;
};

/**
 * Shared empties, so a grid with nothing resolved hands back the same reference every call. A fresh `{}` here
 * re-renders every `useSelector` consumer on every action, and recomputes the memoized column view with it.
 */
const EMPTY_COLUMN_SOURCES: Record<number, PaymentGridSource> = {};
const EMPTY_PENDING_SELL_RATES: Record<number, number> = {};
const EMPTY_FALLBACK_RATES: Record<number, PaymentGridFallbackRates> = {};
const EMPTY_LEFT_TERMS: Record<number, number[]> = {};
const EMPTY_TERM_FLAGS: Record<number, true> = {};
const EMPTY_BLANK_TERMS: ReadonlySet<number> = new Set<number>();
const EMPTY_COLUMN_TERMS: number[] = [];

/** The engine each column resolved to, by term months. Absent until resolved, cleared on reset. */
export const getPaymentGridColumnSources = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): Record<number, PaymentGridSource> => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    // Cast: columnSources is not on the published PaymentGridData type yet. Drop it once it is.
    return (offerDetails.paymentGridData as GridPaymentGridData)?.columnSources || EMPTY_COLUMN_SOURCES;
};

/**
 * The lender program a grid column is backed by. `programInfoByTerm` is the normal answer, but the Lease grid's build
 * never writes it for the CURRENT term - discovery probes only the non-current terms and per-row responses are barred
 * by `skipProgramInfoUpdate` - so the entry is whatever an unrelated deal-screen action left there, and a mileage or
 * location change empties it. The fallback reads the program the deal is actually priced with, off its selected term.
 *
 * Narrow: current term, Lease, redesign on, grid open, and only when the cached entry is not already valid.
 */
export const getGridColumnProgramInfo = (state: OfferReduxBaseState, offerType?: OfferType, termMonths?: number): any => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const cached = getPaymentProgramInfoByTerm(state, currentOfferType, termMonths);

    if (isExplicitLenderProgramGridActive(state, currentOfferType) && getPaymentGridToggle(state, currentOfferType)) {
        const dealProgram = getDealLenderProgram(state, currentOfferType);
        if (isValidProgram(dealProgram)) return dealProgram;
        // A user-priced active cell leaves the deal with no program of its own, but every other column still prices
        // within the program the grid opened on.
        const dealProgramAtOpen = getPaymentGridDealProgramAtOpen(state, currentOfferType);
        if (isValidProgram(dealProgramAtOpen)) return dealProgramAtOpen;
        return isUserPricedProgram(cached) ? undefined : cached;
    }

    if (isValidProgram(cached)) return cached;

    if (currentOfferType !== LEASE || !isAspPaymentGridRedesignActive(state, currentOfferType)) return cached;

    // Gated on the grid being open: this selector runs on EVERY payment, and with the grid closed an emptied cache
    // entry is a deliberate "send no program" signal from `HONOR_LEASE_MILEAGE_CHANGE` / `LOCATION_RESET_OVERRIDES`.
    // Resurrecting the deal's previous program would price against the old mileage or location and suppress
    // `missingRequiredPSFields`.
    if (!getPaymentGridToggle(state, currentOfferType)) return cached;

    // `getPaymentProgramInfoByTerm` defaults an absent `termMonths` to the deal's own term, so an omitted
    // argument IS the current-term case - which is exactly how `doOverridesExistForOfferType` calls it.
    const selectedTermMonths = getSelectedTermMonths(state, currentOfferType);
    if (termMonths != null && termMonths !== selectedTermMonths) return cached;

    const dealProgram = getSelectedTermByOfferType(state, currentOfferType)?.lenderProgram;
    return isValidProgram(dealProgram) ? dealProgram : cached;
};

/**
 * The grid build in progress - the discovery and every row fanned out from it. Its id is the supersession key: a
 * response carrying a different one belongs to a build this one replaced.
 *
 * `isUserProgramQuotes` is the **Auto Select Program intent** this build went out under, which is no longer the same
 * fact as the deal's endpoint. An open outside the explicit-lender grid, an untick (in place; a tick rebuilds) and a
 * deal-screen endpoint move write it; a cell selection never does. The ASP state an individual CALL went out under
 * travels on the call.
 */
export type { PaymentGridBuild };

export const getPaymentGridBuild = (state: OfferReduxBaseState, offerType?: OfferType): PaymentGridBuild | undefined => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    // Cast for the same reason as getPaymentGridColumnSources: not on the published PaymentGridData type yet.
    return (offerDetails.paymentGridData as GridPaymentGridData)?.build;
};

/** The program the deal was priced on when the explicit-lender grid opened; absent once the grid closes or rebuilds. */
export const getPaymentGridDealProgramAtOpen = (state: OfferReduxBaseState, offerType: OfferType): GenericObject | undefined => {
    if (!isExplicitLenderProgramGridActive(state, offerType) || !getPaymentGridToggle(state, offerType)) return undefined;
    return (getOfferDetails(state, offerType)?.paymentGridData as GridPaymentGridData)?.dealProgramAtOpen;
};

export const getPaymentGridCalculatorPricedDealTerm = (state: OfferReduxBaseState, offerType: OfferType): number | undefined => {
    if (!isExplicitLenderProgramGridActive(state, offerType) || !getPaymentGridToggle(state, offerType)) return undefined;
    return (getOfferDetails(state, offerType)?.paymentGridData as GridPaymentGridData)?.calculatorPricedDealTerm;
};

export const isPaymentGridAwaitingVehicleSwitch = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    return Boolean((getOfferDetails(state, currentOfferType).paymentGridData as GridPaymentGridData)?.awaitingVehicleSwitch);
};

/**
 * Whether Auto Select Program is **off** for the grid now on screen: the grid's own intent, recorded on the build,
 * rather than the deal's endpoint, which a cell selection moves. Falls back to the endpoint when there is no build.
 */
export const getGridAspIsUserProgramQuotes = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    return getPaymentGridBuild(state, currentOfferType)?.isUserProgramQuotes ?? getIsUserProgramQuotes(state, currentOfferType);
};

const gridIsUserProgramQuotes = (state: OfferReduxBaseState, offerType: OfferType, gateOffAnswer: boolean): boolean => {
    if (!isExplicitLenderProgramGridActive(state, offerType)) return gateOffAnswer;

    // Only an explicit Auto Select untick beats the program. `isUserProgramQuotes` is the deal's own engine, which a
    // cell selection moves, and reading it here pinned every undecided column to UPQ with a program still on the deal.
    if (getPaymentGridBuild(state, offerType)?.aspUnticked === true) return true;

    return !hasDealLenderProgram(state, offerType);
};

export const getGridIsUserProgramQuotes = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const currentOfferType = offerType || getCurrentOfferType(state);

    return gridIsUserProgramQuotes(state, currentOfferType, getGridAspIsUserProgramQuotes(state, currentOfferType));
};

export const getMileageChangeIsUserProgramQuotes = (state: OfferReduxBaseState, offerType: OfferType): boolean => {
    if (!getPaymentGridToggle(state, offerType) || isFlexBuyPayment(state)) {
        return getIsUserProgramQuotes(state, offerType);
    }
    return getGridIsUserProgramQuotes(state, offerType);
};

export type MileageChangeResidualKeeper = {
    keepsColumn: (columnIndex: number) => boolean;
    keepsTerm: (termMonths: number) => boolean;
};

const KEEPS_NO_RESIDUAL: MileageChangeResidualKeeper = { keepsColumn: () => false, keepsTerm: () => false };

export const getMileageChangeResidualKeeper = (state: OfferReduxBaseState, offerType: OfferType): MileageChangeResidualKeeper => {
    if (!isUpqResidualKeepOnMileageChangeEnabled(state)) return KEEPS_NO_RESIDUAL;

    const gridData = getOfferDetails(state, offerType)?.paymentGridData as GridPaymentGridData | undefined;
    const dealIsUserProgramQuotes = getMileageChangeIsUserProgramQuotes(state, offerType);
    return {
        keepsColumn: (columnIndex) => keepsColumnResidualOnMileageChange(gridData, columnIndex, dealIsUserProgramQuotes),
        keepsTerm: (termMonths) => keepsTermResidualOnMileageChange(gridData, termMonths, dealIsUserProgramQuotes)
    };
};

/** Sell rates typed into columns with no term yet, keyed by column index, so the value survives a blur. */
export const getPaymentGridPendingSellRates = (state: OfferReduxBaseState, offerType?: OfferType): Record<number, number> => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    return (offerDetails.paymentGridData as GridPaymentGridData)?.pendingSellRates || EMPTY_PENDING_SELL_RATES;
};

export const getPaymentGridCarriedOverrides = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): Record<number, PaymentGridCarriedOverride> | undefined =>
    (getOfferDetails(state, offerType || getCurrentOfferType(state)).paymentGridData as GridPaymentGridData)?.carriedOverrides;

export const getPaymentGridRestoredInputs = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): Record<number, PaymentGridRestoredInputs> | undefined =>
    (getOfferDetails(state, offerType || getCurrentOfferType(state)).paymentGridData as GridPaymentGridData)?.restoredInputs;

/**
 * The rate pair a column carried into a term change, held for its user-program-quotes fallback. The unit is narrowed
 * to the two the grid can label and convert, as `SellRates` narrows a typed one.
 */
export type PaymentGridFallbackRates = {
    sellRate?: number;
    buyRate?: number;
    rateType?: typeof RATE_TYPE.APR | typeof RATE_TYPE.MoneyFactor;
    residual?: GenericObject;
};

/**
 * What the grid's user-program-quotes fallback prices with when program-quotes could not quote a column's new term,
 * keyed by the incoming term months. Cleared once the column prices, and with the grid.
 */
export const getPaymentGridFallbackRates = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): Record<number, PaymentGridFallbackRates> => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    // Cast for the same reason as `columnSources` above: not on the published PaymentGridData type yet.
    return (offerDetails.paymentGridData as GridPaymentGridData)?.fallbackRatesByTerm || EMPTY_FALLBACK_RATES;
};

export const getPaymentGridLeftTerms = (state: OfferReduxBaseState, offerType?: OfferType): Record<number, number[]> => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    return (offerDetails.paymentGridData as GridPaymentGridData)?.leftTermsByTerm || EMPTY_LEFT_TERMS;
};

/**
 * Everything the Lease grid's columns are permitted to show, resolved once, so the cells, the sell rate and the term
 * input cannot form different opinions about one column.
 *
 * `quotedTerms` is an allow-list, not an `Unavailable` deny-list: a close clears `columnSources`, so on a reopen a
 * column is *undecided*, and treating that as displayable showed payments the new build never priced.
 * `requestedTerms` separates "a call is out" (spin) from "this build never asked" (blank). With `isMasked` false every
 * field is a shared empty, so other deal types read this for free.
 */
export type PaymentGridColumnView = {
    isMasked: boolean;
    columnSources: Record<number, PaymentGridSource>;
    /** The only columns permitted to display a payment. */
    quotedTerms: ReadonlySet<number>;
    /** Terms this build has asked about, judged or not. */
    requestedTerms: ReadonlySet<number>;
    /** Terms whose row request is still in flight. */
    loadingTerms: ReadonlySet<number>;
    loadingRows: ReadonlySet<number>;
    /**
     * outstanding. `loadingTerms` cannot answer this: a cell stops being `isLoading` the moment its OWN row settles, so
     * a rate, term or residual edit painted its column one cell at a time. Scoped by ROUND rather than by column, or a
     * cash-down edit would grey the whole grid.
     */
    heldTermsByRow: Readonly<Record<number, ReadonlySet<number>>>;
    requestedColumns: ReadonlySet<number>;
    loadingColumns: ReadonlySet<number>;
    heldColumnsByRow: Readonly<Record<number, ReadonlySet<number>>>;
    quotedColumns: ReadonlySet<number>;
};

const EMPTY_HELD_TERMS_BY_ROW: Readonly<Record<number, ReadonlySet<number>>> = {};

const UNMASKED_COLUMN_VIEW: PaymentGridColumnView = {
    isMasked: false,
    columnSources: EMPTY_COLUMN_SOURCES,
    quotedTerms: EMPTY_BLANK_TERMS,
    requestedTerms: EMPTY_BLANK_TERMS,
    loadingTerms: EMPTY_BLANK_TERMS,
    loadingRows: EMPTY_BLANK_TERMS,
    heldTermsByRow: EMPTY_HELD_TERMS_BY_ROW,
    requestedColumns: EMPTY_BLANK_TERMS,
    loadingColumns: EMPTY_BLANK_TERMS,
    heldColumnsByRow: EMPTY_HELD_TERMS_BY_ROW,
    quotedColumns: EMPTY_BLANK_TERMS
};

type RowStatusEntry = {
    isLoading?: boolean;
    terms?: (number | null)[];
    roundId?: string;
    requestedColumns?: number[];
    loadingColumns?: number[];
};

export const getPaymentGridColumnView = createSelector(
    [
        (state: OfferReduxBaseState, offerType?: OfferType) => {
            const resolvedOfferType = offerType || getCurrentOfferType(state);
            return resolvedOfferType === LEASE && isAspPaymentGridRedesignActive(state, resolvedOfferType);
        },
        (state: OfferReduxBaseState, offerType?: OfferType) => getPaymentGridColumnSources(state, offerType),
        (state: OfferReduxBaseState, offerType?: OfferType) => getRowsStatuses(state, offerType),
        (state: OfferReduxBaseState, offerType?: OfferType) => getPaymentGridDecidedColumns(state, offerType)
    ],
    (isMasked, columnSources, rowsStatuses, decidedColumns): PaymentGridColumnView => {
        if (!isMasked) return UNMASKED_COLUMN_VIEW;

        const requestedTerms = new Set<number>();
        const loadingTerms = new Set<number>();
        const loadingRows = new Set<number>();
        const rowKeys = Object.keys(rowsStatuses);
        const entryFor = (key: string): RowStatusEntry | undefined => (rowsStatuses as Record<number, RowStatusEntry>)[Number(key)];

        // Pass 1: which fan-outs still have a call outstanding. A row with no round id contributes nothing.
        const inFlightRounds = new Set<string>();
        rowKeys.forEach((key) => {
            const rowStatus = entryFor(key);
            if (rowStatus?.isLoading && rowStatus.roundId) inFlightRounds.add(rowStatus.roundId);
        });

        const heldTermsByRow: Record<number, ReadonlySet<number>> = {};
        const requestedColumns = new Set<number>();
        const loadingColumns = new Set<number>();
        const heldColumnsByRow: Record<number, ReadonlySet<number>> = {};

        rowKeys.forEach((key) => {
            const rowStatus = entryFor(key);
            // `terms` is replaced per row on every write, so this is the current round's request rather than a history.
            const terms = rowStatus?.terms ?? [];
            const numericTerms = terms.filter((term): term is number => typeof term === 'number');

            numericTerms.forEach((term) => {
                requestedTerms.add(term);
                if (rowStatus?.isLoading) loadingTerms.add(term);
            });
            if (rowStatus?.isLoading) loadingRows.add(Number(key));

            // A row whose own fan-out is still working holds every cell that fan-out asked about, including the ones
            // this row has already answered.
            if (rowStatus?.roundId && inFlightRounds.has(rowStatus.roundId) && numericTerms.length) {
                heldTermsByRow[Number(key)] = new Set(numericTerms);
            }

            (rowStatus?.requestedColumns ?? []).forEach((columnIndex) => requestedColumns.add(columnIndex));
            (rowStatus?.loadingColumns ?? []).forEach((columnIndex) => loadingColumns.add(columnIndex));
            if (rowStatus?.roundId && inFlightRounds.has(rowStatus.roundId) && rowStatus.requestedColumns?.length) {
                heldColumnsByRow[Number(key)] = new Set(rowStatus.requestedColumns);
            }
        });

        return {
            isMasked: true,
            columnSources,
            quotedTerms: new Set(getQuotedTerms(columnSources)),
            requestedTerms,
            loadingTerms,
            loadingRows,
            heldTermsByRow: Object.keys(heldTermsByRow).length ? heldTermsByRow : EMPTY_HELD_TERMS_BY_ROW,
            requestedColumns,
            loadingColumns,
            heldColumnsByRow: Object.keys(heldColumnsByRow).length ? heldColumnsByRow : EMPTY_HELD_TERMS_BY_ROW,
            quotedColumns: new Set(getQuotedTerms(decidedColumns))
        };
    }
);

/**
 * Whether the user has typed a sell rate for this column. It is what makes a column with no program priceable at all,
 * and what tells a column that was *attempted and failed* apart from one that was never priced - only the former
 * carries a validation message.
 */
export const hasUserSuppliedGridSellRate = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    termMonths: number | undefined
): boolean => typeof termMonths === 'number' && getSellRateOverrideByOfferTypeAndTerm(state, offerType, termMonths) != null;

export const getPaymentGridUserSuppliedSellRateTerms = (state: OfferReduxBaseState, offerType?: OfferType): number[] => {
    const resolvedOfferType = offerType || getCurrentOfferType(state);

    return getPaymentGridTerms(state, resolvedOfferType).filter((termMonths): termMonths is number =>
        hasUserSuppliedGridSellRate(state, resolvedOfferType, termMonths ?? undefined)
    );
};

/** The terms the user typed a sell rate into, in this grid session. Cleared with the grid, unlike the override it refers to. */
export const getPaymentGridUserPricedTerms = (state: OfferReduxBaseState, offerType?: OfferType): Record<number, true> => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    return (offerDetails.paymentGridData as GridPaymentGridData)?.userPricedTerms || EMPTY_TERM_FLAGS;
};

const EMPTY_USER_ENTERED_COLUMNS: number[] = [];

export const getPaymentGridUserEnteredColumns = (state: OfferReduxBaseState, offerType?: OfferType): number[] => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    return (offerDetails.paymentGridData as GridPaymentGridData)?.userEnteredColumns || EMPTY_USER_ENTERED_COLUMNS;
};

export const getPaymentGridUserEnteredTerms = (state: OfferReduxBaseState, offerType?: OfferType): Record<number, true> => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    return (offerDetails.paymentGridData as GridPaymentGridData)?.userEnteredTerms || EMPTY_TERM_FLAGS;
};

export const getPaymentGridOriginalColumnSources = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): Record<number, PaymentGridSource> => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    return (offerDetails.paymentGridData as GridPaymentGridData)?.originalColumnSources || EMPTY_COLUMN_SOURCES;
};

const EMPTY_COLUMN_PROGRAMS: Record<number, GenericObject> = {};

export const getPaymentGridOriginalColumnPrograms = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): Record<number, GenericObject> => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    return (offerDetails.paymentGridData as GridPaymentGridData)?.originalColumnPrograms || EMPTY_COLUMN_PROGRAMS;
};

export const getPaymentGridRefusedTerms = (state: OfferReduxBaseState, offerType?: OfferType): Record<number, true> => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    return (offerDetails.paymentGridData as GridPaymentGridData)?.refusedTerms || EMPTY_TERM_FLAGS;
};

export const getPaymentGridPairRejectedTerms = (state: OfferReduxBaseState, offerType?: OfferType): Record<number, true> => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const offerDetails = getOfferDetails(state, currentOfferType);
    return (offerDetails.paymentGridData as GridPaymentGridData)?.pairRejectedTerms || EMPTY_TERM_FLAGS;
};

/**
 * Whether this column carries a residual a grid edit would have written. Narrow on purpose, exactly as the rate half
 * is: `overridesByTerm[T].residualInfo` has many writers, and a record holding only `adjustedResidual*` from a reopened
 * deal would otherwise report the column user-priced with no user figure behind it.
 */
export const hasGridColumnOwnResidual = (residualInfo?: GenericObject): boolean =>
    residualInfo?.programResidualPercent != null || residualInfo?.programResidual != null;

export const hasUserSuppliedGridResidual = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    termMonths: number | undefined
): boolean => {
    if (typeof termMonths !== 'number') return false;
    return hasGridColumnOwnResidual(getResidualInfoOverrideByOfferTypeTerm(state, offerType, termMonths));
};

export const hasGridColumnPricingResidual = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    termMonths: number | undefined
): boolean => {
    if (hasUserSuppliedGridResidual(state, offerType, termMonths)) return true;
    if (typeof termMonths !== 'number' || termMonths !== getSelectedTermMonths(state, offerType)) return false;
    if (offerType !== getCurrentOfferType(state)) return false;
    return (
        getProgramResidualOverrideForSelectedTerm(state, 'programResidualPercent') != null ||
        getProgramResidualOverrideForSelectedTerm(state, 'programResidual') != null
    );
};

export const getPaymentGridStuckColumnOrigin = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    termMonths: number
): PaymentGridSource | undefined => {
    if (getPaymentGridColumnSources(state, offerType)[termMonths] !== PaymentGridSource.Unavailable) return undefined;
    if (offerType === LEASE && getPaymentGridRefusedTerms(state, offerType)[termMonths] !== true) return undefined;
    return getPaymentGridOriginalColumnSources(state, offerType)[termMonths] as PaymentGridSource | undefined;
};

export const getPaymentGridProgramBackedTerms = (state: OfferReduxBaseState, offerType?: OfferType): number[] => {
    const resolvedOfferType = offerType || getCurrentOfferType(state);
    if (!isExplicitLenderProgramGridActive(state, resolvedOfferType)) return EMPTY_COLUMN_TERMS;

    const columnSources = getPaymentGridColumnSources(state, resolvedOfferType);
    const dealIsUserProgramQuotes = getGridIsUserProgramQuotes(state, resolvedOfferType);

    const programBacked = getPaymentGridTerms(state, resolvedOfferType).filter(
        (termMonths): termMonths is number =>
            typeof termMonths === 'number' &&
            !isUserProgramQuotesColumn(
                getPaymentGridStuckColumnOrigin(state, resolvedOfferType, termMonths) ?? columnSources[termMonths],
                dealIsUserProgramQuotes
            )
    );
    return programBacked.length ? programBacked : EMPTY_COLUMN_TERMS;
};

/** The grid's own terms carrying a user-supplied residual, for consumers that need the whole row at once. */
export const getPaymentGridUserSuppliedResidualTerms = (state: OfferReduxBaseState, offerType?: OfferType): number[] => {
    const resolvedOfferType = offerType || getCurrentOfferType(state);

    return getPaymentGridTerms(state, resolvedOfferType).filter((termMonths): termMonths is number =>
        hasUserSuppliedGridResidual(state, resolvedOfferType, termMonths ?? undefined)
    );
};

/**
 * Whether the user priced this column themselves, which is what lets a typed figure override the column's lender
 * program. Both halves are needed: the marker alone is a claim with nothing behind it, and `overridesByTerm[T]` alone is
 * too broad - a deal-screen edit, a restore or a reload all write it, and reading those as intent would unpin the
 * current-term column's program.
 *
 * A typed residual counts alongside a typed rate (DE762937). The marker is written for either, so requiring a sell rate
 * for the second half let the resolution pass hand a residual-priced column back to program-quotes on the next
 * re-price, undoing the re-point the marker had just earned it.
 */
export const hasUserPricedGridColumn = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    termMonths: number | undefined
): boolean =>
    typeof termMonths === 'number' &&
    getPaymentGridUserPricedTerms(state, offerType)[termMonths] === true &&
    (hasUserSuppliedGridSellRate(state, offerType, termMonths) || hasUserSuppliedGridResidual(state, offerType, termMonths));

export type PaymentGridColumnCell = {
    monthlyPayment?: number;
    lenderCode?: string;
    term?: number;
    downPayment?: number;
    [key: string]: unknown;
};

const EMPTY_COLUMN_CELLS: Record<number, Record<number, PaymentGridColumnCell>> = {};

export const getPaymentGridColumnCells = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): Record<number, Record<number, PaymentGridColumnCell>> => {
    const offerDetails = getOfferDetails(state, offerType || getCurrentOfferType(state));
    return (offerDetails.paymentGridData as GridPaymentGridData)?.columnCells || EMPTY_COLUMN_CELLS;
};

export type PaymentGridColumnOverride = {
    sellRate?: number;
    rateType?: string;
    residualInfo?: any;
    residualValueChanged?: boolean;
    term?: number | null;
};

const EMPTY_COLUMN_OVERRIDES: Record<number, PaymentGridColumnOverride> = {};

export const getPaymentGridColumnOverrides = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): Record<number, PaymentGridColumnOverride> => {
    const offerDetails = getOfferDetails(state, offerType || getCurrentOfferType(state));
    return (offerDetails.paymentGridData as GridPaymentGridData)?.columnOverrides || EMPTY_COLUMN_OVERRIDES;
};

export const getPaymentGridDecidedColumns = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): Record<number, PaymentGridSource> => {
    const offerDetails = getOfferDetails(state, offerType || getCurrentOfferType(state));
    return (offerDetails.paymentGridData as GridPaymentGridData)?.decidedColumns || EMPTY_COLUMN_SOURCES;
};

const EMPTY_ERRORED_COLUMNS: number[] = [];

export const getPaymentGridErroredColumns = (state: OfferReduxBaseState, offerType?: OfferType): number[] => {
    const offerDetails = getOfferDetails(state, offerType || getCurrentOfferType(state));
    const paymentGridData = offerDetails.paymentGridData as GridPaymentGridData;
    const erroredColumns = paymentGridData?.erroredColumns;
    if (!erroredColumns?.length) return EMPTY_ERRORED_COLUMNS;

    const rowsStatuses = (paymentGridData.rowsStatuses ?? {}) as Record<number, { loadingColumns?: number[] }>;
    const inFlightColumns = new Set(Object.values(rowsStatuses).flatMap((rowStatus) => rowStatus?.loadingColumns ?? []));
    if (!inFlightColumns.size) return erroredColumns;

    const settledColumns = erroredColumns.filter((columnIndex) => !inFlightColumns.has(columnIndex));
    return settledColumns.length ? settledColumns : EMPTY_ERRORED_COLUMNS;
};

export const getOwnColumnOverride = (
    state: OfferReduxBaseState,
    offerType: OfferType | undefined,
    columnIndex: number,
    termMonths: number | null | undefined
): PaymentGridColumnOverride | undefined => {
    const entry = getPaymentGridColumnOverrides(state, offerType)[columnIndex];
    if (!entry) return undefined;
    if (entry.term != null && entry.term !== termMonths) return undefined;
    return entry;
};

export type PaymentGridOverriddenInputs = {
    sellRate: boolean[];
    residual: boolean[];
};

const PAYMENT_GRID_OVERRIDDEN_INPUT_FIELDS = ['sellRate', 'residual'] as const;

export const getPaymentGridOverriddenInputs = createSelector(
    [
        (state: OfferReduxBaseState, offerType?: OfferType) => offerType || getCurrentOfferType(state),
        (state: OfferReduxBaseState, offerType?: OfferType) =>
            getOfferDetails(state, offerType || getCurrentOfferType(state)).paymentGridData as GridPaymentGridData | undefined,
        (state: OfferReduxBaseState, offerType?: OfferType) =>
            getOfferDetails(state, offerType || getCurrentOfferType(state)).overridesByTerm as Record<number, any> | undefined
    ],
    (offerType, gridData, overridesByTerm): PaymentGridOverriddenInputs => {
        const terms = (gridData?.terms ?? []) as (number | null)[];
        const columnOverrides = (gridData?.columnOverrides ?? EMPTY_COLUMN_OVERRIDES) as Record<number, PaymentGridColumnOverride>;
        const pendingSellRates = gridData?.pendingSellRates ?? EMPTY_PENDING_SELL_RATES;
        const restoredInputs = gridData?.restoredInputs ?? {};
        const carriedOverrides = gridData?.carriedOverrides ?? {};

        const ownOverride = (columnIndex: number) => {
            const entry = columnOverrides[columnIndex];
            return appliesToTerm(entry, terms[columnIndex]) ? entry : undefined;
        };
        const isRestoredAsOverride = (columnIndex: number, field: keyof PaymentGridOverriddenInputs) => {
            const restored = restoredInputs[columnIndex];
            return restored?.term !== terms[columnIndex] || restored[field] !== false;
        };
        const carriedOverride = (columnIndex: number) => {
            const carried = carriedOverrides[columnIndex];
            return carried && carried.term === terms[columnIndex] ? carried : undefined;
        };
        const isSellRateCarried = (columnIndex: number) => {
            const carried = carriedOverride(columnIndex);
            return carried?.sellRate != null && overridesByTerm?.[carried.term]?.sellRate === carried.sellRate;
        };
        const isResidualCarried = (columnIndex: number) => {
            const carried = carriedOverride(columnIndex)?.residual;
            const termMonths = terms[columnIndex] as number;
            return carried != null && overridesByTerm?.[termMonths]?.residualInfo?.[carried.valueKey] === carried.value;
        };

        return {
            sellRate: terms.map(
                (_term, columnIndex) =>
                    (ownOverride(columnIndex)?.sellRate != null && isRestoredAsOverride(columnIndex, 'sellRate')) ||
                    pendingSellRates[columnIndex] != null ||
                    isSellRateCarried(columnIndex)
            ),
            residual: terms.map((_term, columnIndex) => {
                const own = ownOverride(columnIndex);
                return (
                    offerType === LEASE &&
                    ((own?.residualInfo != null &&
                        own.residualValueChanged !== false &&
                        isRestoredAsOverride(columnIndex, 'residual')) ||
                        isResidualCarried(columnIndex))
                );
            })
        };
    }
);

export const getPaymentGridCarriedOverride = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    columnIndex: number,
    overridden: { sellRate: boolean; residual: boolean }
): PaymentGridCarriedOverride | undefined => {
    const offerDetails = getOfferDetails(state, offerType);
    const termMonths = (offerDetails.paymentGridData as GridPaymentGridData | undefined)?.terms?.[columnIndex];
    if (termMonths == null) return undefined;
    const termOverride = (offerDetails.overridesByTerm as Record<number, any> | undefined)?.[termMonths];
    const sellRate: number | undefined = overridden.sellRate ? termOverride?.sellRate ?? undefined : undefined;
    const residualInfo = overridden.residual && offerType === LEASE ? termOverride?.residualInfo : undefined;
    const valueKey = residualInfo?.baseResidualType === RESIDUAL_DOLLAR ? 'programResidual' : 'programResidualPercent';
    const residualValue: number | undefined = residualInfo?.[valueKey] ?? undefined;
    if (sellRate == null && residualValue == null) return undefined;
    return {
        term: termMonths,
        ...(sellRate != null && { sellRate }),
        ...(residualValue != null && { residual: { valueKey, value: residualValue } })
    };
};

export const getPaymentGridOverriddenInputIndices = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): PaymentGridOverriddenInputIndices => {
    const overridden = getPaymentGridOverriddenInputs(state, offerType);
    return PAYMENT_GRID_OVERRIDDEN_INPUT_FIELDS.reduce<PaymentGridOverriddenInputIndices>((indices, field) => {
        const fieldIndices = overridden[field].reduce<number[]>((flagged, isOverridden, index) => {
            if (isOverridden) flagged.push(index);
            return flagged;
        }, []);
        if (fieldIndices.length) indices[field] = fieldIndices;
        return indices;
    }, {});
};

/**
 * The active cell's recorded ROW, off the stamp - the only record. A bare `selectedRowIndex` beside it held the same
 * number and drifted from it five times. The baggage still emits both indices for Sales View, derived from the stamp.
 */
export const getPaymentGridSelectedRowIndex = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getStampedActiveCell(state, offerType || getCurrentOfferType(state))?.rowIndex;

const getStampedActiveCell = (
    state: OfferReduxBaseState,
    offerType: OfferType
): { columnIndex?: number; term?: number; rowIndex?: number; downPayment?: number } | undefined =>
    (getOfferDetails(state, offerType).paymentGridData as any)?.activeCell;

export const getResolvedPaymentGridSelectedRowIndex = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const dealDownPayment =
        getEquityAllocationGridCashDown(state, currentOfferType) ?? getDownPaymentByOfferType(state, currentOfferType);
    if (dealDownPayment == null) return undefined;

    const cashDown = getPaymentGridCashDown(state, currentOfferType);

    const stamped = getStampedActiveCell(state, currentOfferType);
    if (stamped?.rowIndex != null && cashDown[stamped.rowIndex] != null && cashDown[stamped.rowIndex] === stamped.downPayment)
        return stamped.rowIndex;

    const firstMatch = cashDown.indexOf(dealDownPayment);
    return firstMatch === -1 ? undefined : firstMatch;
};

export const getPaymentGridSelectedColumnIndex = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined =>
    getStampedActiveCell(state, offerType || getCurrentOfferType(state))?.columnIndex;

export const getResolvedPaymentGridSelectedColumnIndex = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): number | undefined => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const selectedTermMonths = getSelectedTermMonths(state, currentOfferType);
    if (!selectedTermMonths) return undefined;

    const terms = getPaymentGridTerms(state, currentOfferType);

    const stamped = getStampedActiveCell(state, currentOfferType);
    if (stamped?.columnIndex != null && terms[stamped.columnIndex] != null && terms[stamped.columnIndex] === stamped.term)
        return stamped.columnIndex;

    const firstMatch = terms.indexOf(selectedTermMonths);
    return firstMatch === -1 ? undefined : firstMatch;
};

export const getPaymentGridDealColumnIndex = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    return resolveDealColumnIndex(
        getPaymentGridTerms(state, currentOfferType),
        getStampedActiveCell(state, currentOfferType)?.columnIndex,
        getSelectedTermMonths(state, currentOfferType)
    );
};

export const getPaymentGridTermOwnerColumnIndex = (state: OfferReduxBaseState, offerType: OfferType): number | undefined =>
    resolveTermOwnerColumnIndex(
        getPaymentGridTerms(state, offerType),
        getStampedActiveCell(state, offerType)?.columnIndex,
        getSelectedTermMonths(state, offerType)
    );

export const hasUserActedOnGridColumn = (state: OfferReduxBaseState, offerType: OfferType, termMonths: number): boolean =>
    getPaymentGridUserEnteredTerms(state, offerType)[termMonths] === true ||
    getPaymentGridUserPricedTerms(state, offerType)[termMonths] === true ||
    hasUserSuppliedGridSellRate(state, offerType, termMonths) ||
    hasUserSuppliedGridResidual(state, offerType, termMonths);

export const getGridColumnPairHalves = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    columnIndex: number | undefined,
    termMonths: number
): { hasRate: boolean; hasResidual: boolean } => {
    const isOwnColumnJudged =
        typeof columnIndex === 'number' &&
        isTermDuplicated(getPaymentGridTerms(state, offerType), termMonths) &&
        getPaymentGridTermOwnerColumnIndex(state, offerType) !== columnIndex;

    if (!isOwnColumnJudged) {
        return {
            hasRate: hasUserSuppliedGridSellRate(state, offerType, termMonths),
            hasResidual: hasGridColumnPricingResidual(state, offerType, termMonths)
        };
    }

    const ownOverride = getOwnColumnOverride(state, offerType, columnIndex as number, termMonths);
    return {
        hasRate: ownOverride?.sellRate != null,
        hasResidual: hasGridColumnOwnResidual(ownOverride?.residualInfo)
    };
};

const columnPairFacts = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    termMonths: number,
    quotedTerms: ReadonlySet<number>,
    dealFacts: { columnSources: Record<number, PaymentGridSource>; dealIsUserProgramQuotes: boolean },
    columnIndex?: number
): ColumnPairFacts => {
    const { hasRate, hasResidual } = getGridColumnPairHalves(state, offerType, columnIndex, termMonths);
    if (isSharedTermSiblingColumn(state, offerType, getPaymentGridTerms(state, offerType), columnIndex, termMonths)) {
        const ownVerdict = getPaymentGridDecidedColumns(state, offerType)[columnIndex as number] as PaymentGridSource | undefined;
        return {
            source: ownVerdict,
            isUserProgramQuotesEngine: isUserProgramQuotesColumn(ownVerdict, dealFacts.dealIsUserProgramQuotes),
            hasUserSuppliedRate: hasRate,
            hasUserSuppliedResidual: hasResidual,
            isQuoted: ownVerdict === PaymentGridSource.ProgramQuotes || ownVerdict === PaymentGridSource.UserProgramQuotes,
            isRefused: hasRate && hasResidual && getPaymentGridErroredColumns(state, offerType).includes(columnIndex as number)
        };
    }
    const stuckOrigin = getPaymentGridStuckColumnOrigin(state, offerType, termMonths);
    const source = stuckOrigin ?? dealFacts.columnSources[termMonths];
    return {
        source,
        isUserProgramQuotesEngine: isUserProgramQuotesColumn(source, dealFacts.dealIsUserProgramQuotes),
        hasUserSuppliedRate: hasRate,
        hasUserSuppliedResidual: hasResidual,
        isQuoted: quotedTerms.has(termMonths),
        isRefused: stuckOrigin != null || getPaymentGridPairRejectedTerms(state, offerType)[termMonths] === true
    };
};

const getPaymentGridColumnsBy = (
    state: OfferReduxBaseState,
    offerType: OfferType | undefined,
    predicate: (facts: ColumnPairFacts) => boolean
): number[] => {
    const resolvedOfferType = offerType || getCurrentOfferType(state);
    if (resolvedOfferType !== LEASE || !isAspPaymentGridRedesignActive(state, resolvedOfferType)) return EMPTY_COLUMN_TERMS;

    const { quotedTerms } = getPaymentGridColumnView(state, resolvedOfferType);
    const dealFacts = {
        columnSources: getPaymentGridColumnSources(state, resolvedOfferType),
        dealIsUserProgramQuotes: getGridIsUserProgramQuotes(state, resolvedOfferType)
    };
    const matching = getPaymentGridTerms(state, resolvedOfferType).reduce<number[]>((columns, termMonths, columnIndex) => {
        if (typeof termMonths !== 'number' || termMonths <= 0) return columns;
        if (predicate(columnPairFacts(state, resolvedOfferType, termMonths, quotedTerms, dealFacts, columnIndex))) {
            columns.push(columnIndex);
        }
        return columns;
    }, []);
    return matching.length ? matching : EMPTY_COLUMN_INDICES;
};

const EMPTY_COLUMN_INDICES: number[] = [];

export const getPaymentGridColumnIndicesPairRejected = (state: OfferReduxBaseState, offerType?: OfferType): number[] =>
    getPaymentGridColumnsBy(state, offerType, isColumnPairRejected);

export const getPaymentGridColumnsPairRejected = (state: OfferReduxBaseState, offerType?: OfferType): number[] => {
    const columns = getPaymentGridColumnIndicesPairRejected(state, offerType);
    if (!columns.length) return EMPTY_COLUMN_TERMS;
    const gridTerms = getPaymentGridTerms(state, offerType || getCurrentOfferType(state));
    return columns.map((columnIndex) => gridTerms[columnIndex] as number);
};

const columnIndicesAwaitingPair = (
    state: OfferReduxBaseState,
    offerType: OfferType | undefined,
    includeBuildBlank: boolean
): number[] => {
    const resolvedOfferType = offerType || getCurrentOfferType(state);
    if (resolvedOfferType !== LEASE || !isAspPaymentGridRedesignActive(state, resolvedOfferType)) return EMPTY_COLUMN_INDICES;

    const { quotedTerms } = getPaymentGridColumnView(state, resolvedOfferType);
    const dealFacts = {
        columnSources: getPaymentGridColumnSources(state, resolvedOfferType),
        dealIsUserProgramQuotes: getGridIsUserProgramQuotes(state, resolvedOfferType)
    };
    const gridTerms = getPaymentGridTerms(state, resolvedOfferType);
    const userEnteredColumns = getPaymentGridUserEnteredColumns(state, resolvedOfferType);
    const matching = gridTerms.reduce<number[]>((indices, termMonths, columnIndex) => {
        if (typeof termMonths !== 'number' || termMonths <= 0) return indices;
        const facts = columnPairFacts(state, resolvedOfferType, termMonths, quotedTerms, dealFacts, columnIndex);
        const ownOverride = getOwnColumnOverride(state, resolvedOfferType, columnIndex, termMonths);
        const hasActed =
            (isSharedTermSiblingColumn(state, resolvedOfferType, gridTerms, columnIndex, termMonths)
                ? userEnteredColumns.includes(columnIndex)
                : hasUserActedOnGridColumn(state, resolvedOfferType, termMonths)) ||
            ownOverride?.sellRate != null ||
            hasGridColumnOwnResidual(ownOverride?.residualInfo);
        const isBuildBlankUnderAsp = facts.source === PaymentGridSource.Unavailable && !dealFacts.dealIsUserProgramQuotes;
        if (isColumnAwaitingPair(facts) && (hasActed || (includeBuildBlank && isBuildBlankUnderAsp))) {
            indices.push(columnIndex);
        }
        return indices;
    }, []);
    return matching.length ? matching : EMPTY_COLUMN_INDICES;
};

export const getPaymentGridColumnIndicesAwaitingPair = (state: OfferReduxBaseState, offerType?: OfferType): number[] =>
    columnIndicesAwaitingPair(state, offerType, true);

export const hasPaymentGridColumnMessage = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const resolvedOfferType = offerType || getCurrentOfferType(state);
    const gridTerms = getPaymentGridTerms(state, resolvedOfferType);
    const speaking = [
        ...columnIndicesAwaitingPair(state, offerType, false).map((columnIndex) => gridTerms[columnIndex] as number),
        ...getPaymentGridColumnsPairRejected(state, offerType)
    ];
    if (!speaking.length) return false;

    const { loadingTerms } = getPaymentGridColumnView(state, offerType);
    return speaking.some((termMonths) => !loadingTerms.has(termMonths));
};

/** The engine that produced the selected cell's payment, so a fallback-priced cell does not present itself as program-backed. */
export const getSelectedPaymentGridCellSource = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): PaymentGridSource | undefined => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    if (!isAspPaymentGridRedesignActive(state, currentOfferType)) return undefined;
    // Only while the grid is open: a stale entry must not keep driving how the checkbox reads.
    if (!getPaymentGridToggle(state, currentOfferType)) return undefined;

    const selectedTermMonths = getSelectedTermMonths(state, currentOfferType);
    if (!selectedTermMonths) return undefined;

    return resolveCellVerdict(
        getPaymentGridTerms(state, currentOfferType),
        getPaymentGridDecidedColumns(state, currentOfferType) as Record<number, PaymentGridSource>,
        getPaymentGridColumnSources(state, currentOfferType),
        getPaymentGridDealColumnIndex(state, currentOfferType),
        selectedTermMonths
    );
};

export const isSelectedPaymentGridCellSourcePending = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const currentOfferType = offerType || getCurrentOfferType(state);

    return (
        Boolean(getPaymentGridDealTermBeforeEdit(state, currentOfferType)) &&
        getSelectedPaymentGridCellSource(state, currentOfferType) === undefined &&
        currentOfferType === LEASE &&
        isExplicitLenderProgramGridActive(state, currentOfferType) &&
        getPaymentGridToggle(state, currentOfferType)
    );
};

/** Render gate: true while any cell that has a term is still without a payment. Columns with no term are excluded, or a short dealer term list would hold the grid forever. */
export const isPaymentGridResolving = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    // Lease and Balloon can leave a cell unquoted by design and never populate columnSources, so without this
    // gate the grid would wait forever for a cell that was never going to be priced.
    if (!isAspPaymentGridRedesignActive(state, currentOfferType)) return false;

    // Lease needs no grid-wide gate - each column carries its own verdict and an unquoted one renders blank. Finance
    // keeps it: its fallback pass can still replace a column's first response moments later.
    if (currentOfferType === LEASE) return false;

    const columnSources = getPaymentGridColumnSources(state, currentOfferType);
    const terms = getPaymentGridTerms(state, currentOfferType);
    const downPayments = getPaymentGridCashDown(state, currentOfferType);
    const decidedColumns = getPaymentGridDecidedColumns(state, currentOfferType);
    const columnCells = getPaymentGridColumnCells(state, currentOfferType);
    const holdersOf = (termMonths: number) =>
        terms.reduce<number[]>((held, term, columnIndex) => (term === termMonths ? [...held, columnIndex] : held), []);
    const isHolderUnavailable = (columnIndex: number, termMonths: number) =>
        (decidedColumns[columnIndex] ?? columnSources[termMonths]) === PaymentGridSource.Unavailable;
    const hasTermRecord = (termMonths: number, downPayment: number) => {
        const termRecord = getTerm(state, getTermId(currentOfferType, termMonths, downPayment)!);
        return Boolean(termRecord) && !isEmpty(termRecord);
    };
    const hasOwnQuote = (columnIndex: number, termMonths: number, downPayment: number) =>
        downPayments.some((rowDownPayment, rowIndex) => {
            const cell = columnCells[columnIndex]?.[rowIndex];
            return rowDownPayment === downPayment && isCellPricedFor(cell, termMonths, downPayment) && cell?.monthlyPayment != null;
        });

    const unavailableTerms = [...new Set(terms.filter((term): term is number => typeof term === 'number'))].filter((termMonths) =>
        isTermDuplicated(terms, termMonths)
            ? holdersOf(termMonths).every((columnIndex) => isHolderUnavailable(columnIndex, termMonths))
            : columnSources[termMonths] === PaymentGridSource.Unavailable
    );

    return !isGridComplete({
        termMonthsList: terms,
        downPaymentList: downPayments,
        unavailableTerms,
        hasQuoteForCell: (termMonths, downPayment) =>
            isTermDuplicated(terms, termMonths)
                ? holdersOf(termMonths).every(
                      (columnIndex) =>
                          isHolderUnavailable(columnIndex, termMonths) ||
                          hasOwnQuote(columnIndex, termMonths, downPayment) ||
                          hasTermRecord(termMonths, downPayment)
                  )
                : hasTermRecord(termMonths, downPayment)
    });
};

export const getCreditTiersForGrid = (state: OfferReduxBaseState, offerType?: OfferType) => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const tierOfferType = currentOfferType === BALLOON ? FINANCE : currentOfferType;
    return state?.dealer?.creditTiers?.[tierOfferType] || [];
};

export const getTermLengthsForCurrentCreditTier = (state: OfferReduxBaseState, offerType?: OfferType): number[] => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    if (currentOfferType === CASH) {
        return [];
    }
    const offerDetails = getOfferDetails(state, currentOfferType);
    const selectedCreditTierId = offerDetails.creditTier;
    const creditTiers = getCreditTiersForGrid(state, currentOfferType);
    const selectedTierInfo = creditTiers.find((tier: any) => {
        if (tier.id === selectedCreditTierId) {
            return tier;
        }
    });
    return selectedTierInfo!.terms.map(({ months }: { months: number }) => months);
};

export const getInitialTermsMonthsListForGrid = (state: OfferReduxBaseState, offerType?: OfferType) => {
    const currentTermMonth = getSelectedTerm(state, offerType).months;
    const allTerms = getTermLengthsForCurrentCreditTier(state, offerType);

    if (!allTerms.includes(currentTermMonth)) {
        allTerms.push(currentTermMonth);
    }

    return getGridTerms(allTerms, currentTermMonth);
};

export const getEquityAllocationGridCashDown = (state: OfferReduxBaseState, offerType?: OfferType): number | undefined => {
    const resolvedOfferType = offerType || getCurrentOfferType(state);
    if (resolvedOfferType !== LEASE || !getFeatureToggle(state, 'enableEquityAllocationOrder')) {
        return undefined;
    }

    const selectedTerm = getSelectedTerm(state, LEASE);
    if (!selectedTerm || isEmpty(selectedTerm)) {
        return undefined;
    }

    // One-pay lease always reads capReductionCash (net cash down).
    if (isOnePayLeaseFrequency(selectedTerm.paymentFrequency as PaymentSchedule | undefined)) {
        return selectedTerm.netCashDown ?? 0;
    }

    const equityAllocationMethod = getOfferDetails(state, LEASE)?.equityAllocationMethod;
    const hasCash = typeof equityAllocationMethod === 'string' && isCashEquityAllocationMethod(equityAllocationMethod);

    // Equity contains "Cash" -> Cash Due Today (dueAtSigning); otherwise -> Cash Down (cashDueFromCustomer).
    return hasCash ? selectedTerm.dueAtSigning ?? 0 : selectedTerm.cashDueFromCustomer ?? 0;
};

export const getInitialDownPaymentListForGrid = (state: OfferReduxBaseState, offerType?: OfferType) => {
    const resolvedOfferType = offerType || getCurrentOfferType(state);
    const equityAllocationCashDown = getEquityAllocationGridCashDown(state, resolvedOfferType);
    const currentDownPayment = equityAllocationCashDown ?? getDownPaymentByOfferType(state, resolvedOfferType);
    const middleDownPayment = !currentDownPayment || currentDownPayment === 0 ? DOWN_PAYMENT_DIFFERENCE : currentDownPayment;

    const lowerDownPayment =
        middleDownPayment - DOWN_PAYMENT_DIFFERENCE > 0 ? roundWithPrecision(middleDownPayment - DOWN_PAYMENT_DIFFERENCE) : 0;

    const upperDownPayment = roundWithPrecision(middleDownPayment + DOWN_PAYMENT_DIFFERENCE);

    return [lowerDownPayment, middleDownPayment, upperDownPayment];
};

export const getPaymentGridTermsList = (state: OfferReduxBaseState): TermMonthItem[] => {
    const terms = getPaymentGridTerms(state);
    return terms.map((val, key) => ({ key, val }));
};

/**
 * US2075533: the rate type shown on a grid column. A term priced by a program quoted in a different unit keeps
 * its own stored `rateType`, so the column is labelled MF or APR to match the number it is displaying, instead
 * of inheriting the selected term's from the offer-level `rateTypeOverride`.
 *
 * Display-only by design — `getRateTypeByTerm` also builds the outgoing payment request, where one term node's
 * `rateType` cannot describe a `sellRate` and `buyRate` that may have come from different sources.
 *
 * The toggle is read from the global slice first: `withoutScenarioFeatureToggles` strips `offer.featureToggles`
 * from non-primary scenario hydrations, so the offer-slice read alone would drop the stamp in compare columns.
 */
export const getGridRateTypeByTerm = (state: OfferReduxBaseState, offerType: OfferType, months: number) => {
    const rateTypeForTerm = isGridRedesignEnabled(state)
        ? getOfferDetails(state, offerType).overridesByTerm?.[months]?.rateType
        : undefined;

    return rateTypeForTerm || getRateTypeByTerm(state, offerType, months);
};

const isCellPricedFor = (cell: PaymentGridColumnCell | undefined, termMonths: number, downPayment: number): boolean => {
    if (!cell) return false;
    if (cell.term != null && cell.term !== termMonths) return false;
    return cell.downPayment == null || cell.downPayment === downPayment;
};

const getOwnPricedCellForColumn = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    termMonths: number,
    columnIndex: number
): PaymentGridColumnCell | undefined => {
    const rows = getPaymentGridColumnCells(state, offerType)[columnIndex];
    if (!rows) return undefined;
    const downPayments = getPaymentGridCashDown(state, offerType);
    for (let rowIndex = 0; rowIndex < downPayments.length; rowIndex += 1) {
        const downPayment = downPayments[rowIndex];
        if (downPayment == null) continue;
        const cell = rows[rowIndex];
        if (isCellPricedFor(cell, termMonths, downPayment as number) && Object.keys(cell).length) return cell;
    }
    return undefined;
};

export const getPaymentGridCellRecord = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    columnIndex: number | undefined,
    rowIndex: number | undefined,
    termMonths: number | undefined,
    downPayment: number | undefined
): PaymentGridColumnCell | undefined => {
    if (columnIndex == null || rowIndex == null || termMonths == null || downPayment == null) return undefined;
    const cell = getPaymentGridColumnCells(state, offerType)[columnIndex]?.[rowIndex];
    if (!isCellPricedFor(cell, termMonths, downPayment) || !cell || cell.monthlyPayment == null) return undefined;
    return cell;
};

export type ResolvedPaymentGridActiveCell = { columnIndex: number; rowIndex: number; termMonths: number; downPayment: number };

export const getResolvedPaymentGridActiveCell = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): ResolvedPaymentGridActiveCell | undefined => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    const columnIndex = getResolvedPaymentGridSelectedColumnIndex(state, currentOfferType);
    const rowIndex = getResolvedPaymentGridSelectedRowIndex(state, currentOfferType);
    if (columnIndex == null || rowIndex == null) return undefined;

    const termMonths = getPaymentGridTerms(state, currentOfferType)[columnIndex];
    if (!hasTerm(termMonths)) return undefined;

    return { columnIndex, rowIndex, termMonths, downPayment: getPaymentGridCashDown(state, currentOfferType)[rowIndex] as number };
};

export const getPaymentGridDealTermBeforeEdit = (state: OfferReduxBaseState, offerType?: OfferType): string | undefined =>
    (getOfferDetails(state, offerType || getCurrentOfferType(state)).paymentGridData as GridPaymentGridData)?.dealTermBeforeEdit;

export const getPaymentGridDealTermOverridesBeforeEdit = (
    state: OfferReduxBaseState,
    offerType?: OfferType
): Record<number, GenericObject> | undefined =>
    (getOfferDetails(state, offerType || getCurrentOfferType(state)).paymentGridData as GridPaymentGridData)
        ?.dealTermOverridesBeforeEdit;

export const isDealParkedOffActiveColumn = (state: OfferReduxBaseState, offerType?: OfferType): boolean => {
    const currentOfferType = offerType || getCurrentOfferType(state);
    if (!isAspPaymentGridRedesignActive(state, currentOfferType) || !getPaymentGridToggle(state, currentOfferType)) return false;

    const activeCell = getResolvedPaymentGridActiveCell(state, currentOfferType);
    return activeCell != null && activeCell.termMonths !== getSelectedTermMonths(state, currentOfferType);
};

export const isPaymentGridCellServedFromStoredQuote = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    columnIndex: number,
    rowIndex: number
): boolean => {
    if (!getFeatureToggle(state, PAYMENT_GRID_BAGGAGE_COMPRESSION_TOGGLE as any)) return false;

    const gridTerms = getPaymentGridTerms(state, offerType);
    const gridCashDown = getPaymentGridCashDown(state, offerType);
    const termMonths = gridTerms[columnIndex];
    const downPayment = gridCashDown[rowIndex];
    if (!hasTerm(termMonths) || typeof downPayment !== 'number') return false;

    const isCellShared =
        isAspPaymentGridRedesignActive(state, offerType) &&
        (gridTerms.filter((term) => term != null && term === termMonths).length > 1 ||
            gridCashDown.filter((cashDown) => cashDown != null && cashDown === downPayment).length > 1);
    if (isCellShared && getPaymentGridCellRecord(state, offerType, columnIndex, rowIndex, termMonths, downPayment)) {
        return true;
    }

    const termId = getTermId(offerType, termMonths, downPayment) as string;
    const columnCell = getPaymentGridColumnCells(state, offerType)[columnIndex]?.[rowIndex];
    const pricedCell = isCellPricedFor(columnCell, termMonths, downPayment) ? columnCell : undefined;
    const hasQuote = pricedCell ? pricedCell.monthlyPayment != null : !isEmpty(getTerm(state, termId));

    return hasQuote && getPaymentGridCellPricedAt(state, offerType)[termId] !== undefined;
};

export const getResolvedColumnSellRate = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    columnIndex: number | undefined
): number | undefined => {
    if (columnIndex == null) return undefined;
    const termMonths = getPaymentGridTerms(state, offerType)[columnIndex];
    if (typeof termMonths !== 'number') return undefined;
    const pricedFromResponse = getSellRateByOfferType(state, offerType, termMonths) || undefined;
    return (
        getOwnColumnOverride(state, offerType, columnIndex, termMonths)?.sellRate ??
        (getOwnPricedCellForColumn(state, offerType, termMonths, columnIndex)?.sellRate as number | undefined) ??
        pricedFromResponse
    );
};

const toPricedNumberOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const residualFromCell = (cell: PaymentGridColumnCell | undefined, isDollar: boolean): number | null =>
    isDollar ? toPricedNumberOrNull(cell?.baseResidualDollar) : toPricedNumberOrNull(cell?.residualPercentage);

const wasGridWritten = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    termMonths: number,
    half: 'rate' | 'residual'
): boolean => {
    const offerDetails = getOfferDetails(state, offerType);
    return ((offerDetails.paymentGridData as GridPaymentGridData)?.gridWrittenTerms as any)?.[termMonths]?.[half] === true;
};

const isTermOverrideOwnedByAnotherColumn = (
    state: OfferReduxBaseState,
    offerType: OfferType | undefined,
    terms: (number | null)[],
    columnIndex: number,
    termMonths: number
): boolean => {
    if (getOwnColumnOverride(state, offerType, columnIndex, termMonths)?.sellRate != null) return false;
    return terms.some(
        (term, index) =>
            index !== columnIndex &&
            term === termMonths &&
            getOwnColumnOverride(state, offerType, index, termMonths)?.sellRate != null
    );
};

const isSharedTermSiblingColumn = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    terms: (number | null)[],
    columnIndex: number | undefined,
    termMonths: number
): boolean =>
    typeof columnIndex === 'number' &&
    isTermDuplicated(terms, termMonths) &&
    getPaymentGridTermOwnerColumnIndex(state, offerType) !== columnIndex;

export const isTermDuplicated = (terms: (number | null)[], termMonths: number | null): boolean =>
    termMonths != null && terms.filter((term) => term === termMonths).length > 1;

const isColumnQuotedInView = (
    view: PaymentGridColumnView,
    decidedColumns: Record<number, PaymentGridSource>,
    terms: (number | null)[],
    columnIndex: number,
    termMonths: number
): boolean =>
    decidedColumns[columnIndex] != null && isTermDuplicated(terms, termMonths)
        ? view.quotedColumns.has(columnIndex)
        : view.quotedTerms.has(termMonths);

const readsOnlyOwnColumnRate = (
    state: OfferReduxBaseState,
    offerType: OfferType | undefined,
    terms: (number | null)[],
    columnIndex: number,
    termMonths: number
): boolean =>
    isTermOverrideOwnedByAnotherColumn(state, offerType, terms, columnIndex, termMonths) ||
    (getOwnColumnOverride(state, offerType, columnIndex, termMonths)?.sellRate == null &&
        wasGridWritten(state, offerType as OfferType, termMonths, 'rate')) ||
    isTermDuplicated(terms, termMonths);

type ColumnRateType = typeof RATE_TYPE.APR | typeof RATE_TYPE.MoneyFactor;

const toColumnRateType = (rateType: unknown): ColumnRateType | undefined =>
    rateType === RATE_TYPE.APR || rateType === RATE_TYPE.MoneyFactor ? rateType : undefined;

// Not `isAspPaymentGridRedesignActive`: that is FINANCE-only, and this label matters most on lease, where a
// subvented money-factor program and a standard APR one can price different columns of the same grid.
// Either slice enabling it counts, and deliberately not `??`: the global slice carries an explicit `false`
// while the toggle is unconfigured in AWS, which would shadow a `?enableToggles=` override — those land on
// the offer slice. The global slice is still consulted because `withoutScenarioFeatureToggles` strips the
// offer one from non-primary scenario hydrations, leaving it the only source in compare columns.
const isGridRedesignEnabled = (state: OfferReduxBaseState): boolean =>
    Boolean(state.featureToggles?.[ASP_PG_REDESIGN_TOGGLE]) || Boolean(getFeatureToggle(state, ASP_PG_REDESIGN_TOGGLE));

export const getPaymentGridColumnRateType = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    columnIndex: number
): ColumnRateType | undefined => {
    if (offerType !== LEASE || !isGridRedesignEnabled(state)) return undefined;

    const terms = getPaymentGridTerms(state, offerType);
    const termMonths = terms[columnIndex];
    if (typeof termMonths !== 'number') return undefined;

    const ownOverride = getOwnColumnOverride(state, offerType, columnIndex, termMonths);
    if (ownOverride?.sellRate != null) return toColumnRateType(ownOverride.rateType);

    if (readsOnlyOwnColumnRate(state, offerType, terms, columnIndex, termMonths)) {
        const ownPricedCell = getOwnPricedCellForColumn(state, offerType, termMonths, columnIndex);
        if (ownPricedCell?.sellRate != null) return toColumnRateType(ownPricedCell.rateType);
    } else {
        const termOverride = getOfferDetails(state, offerType).overridesByTerm?.[termMonths];
        const termRateType = toColumnRateType(termOverride?.rateType);
        if (termRateType) return termRateType;
        if (termOverride?.sellRate != null) {
            return (
                toColumnRateType(getOfferDetails(state, offerType).rateTypeOverride) ??
                toColumnRateType(getTermByOfferTypeAndMonths(state, offerType, termMonths)?.rateType)
            );
        }
    }

    return toColumnRateType(getTermByOfferTypeAndMonths(state, offerType, termMonths)?.rateType);
};

export const getPaymentGridTermRateType = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    termMonths: number | undefined
): ColumnRateType | undefined => {
    if (offerType !== LEASE || !isGridRedesignEnabled(state) || termMonths == null) return undefined;

    const termOverride = getOfferDetails(state, offerType).overridesByTerm?.[termMonths];
    if (termOverride?.sellRate != null) return toColumnRateType(termOverride.rateType);

    return toColumnRateType(getTermByOfferTypeAndMonths(state, offerType, termMonths)?.rateType);
};

const getSellRatesFromTerms = (state: OfferReduxBaseState, terms: (number | null)[], percents?: number[]): SellRatesFromTerms => {
    const offerType = getCurrentOfferType(state);
    // Called with one argument deliberately, as every component consumer does: `createSelector`'s cache holds a
    // single entry, so alternating argument shapes would recompute on every call and hand each `useSelector`
    // subscriber a new object - re-rendering the whole rate row on every action. This resolves the same offer
    // type internally.
    const columnView = getPaymentGridColumnView(state);
    const decidedColumns = getPaymentGridDecidedColumns(state, offerType);
    const dealsColumn = isAspPaymentGridRedesignActive(state, offerType)
        ? getPaymentGridTermOwnerColumnIndex(state, offerType)
        : undefined;
    return terms.map((month, index) => {
        if (!month) {
            return { month, val: null as any };
        }
        const ownOverride = getOwnColumnOverride(state, offerType, index, month);
        const isDealsColumnOnSharedTerm = index === dealsColumn && isTermDuplicated(terms, month);
        const isDuplicated = readsOnlyOwnColumnRate(state, offerType, terms, index, month);
        const ownPricedCell = isDuplicated ? getOwnPricedCellForColumn(state, offerType, month, index) : undefined;
        const rateType =
            getPaymentGridColumnRateType(state, offerType as OfferType, index) ??
            (ownOverride?.rateType as ReturnType<typeof getGridRateTypeByTerm>) ??
            (isDuplicated && !isDealsColumnOnSharedTerm
                ? (getRateTypeByTerm(state, offerType, month) as ReturnType<typeof getGridRateTypeByTerm>)
                : getGridRateTypeByTerm(state, offerType, month));

        // On Lease a column shows a rate only once this build has quoted it: the fallback below reads the rate off the
        if (columnView.isMasked && !isColumnQuotedInView(columnView, decidedColumns, terms, index, month)) {
            const ownRate =
                ownOverride?.sellRate ??
                (isDuplicated && !isDealsColumnOnSharedTerm
                    ? undefined
                    : getSellRateOverrideByOfferTypeAndTerm(state, offerType, month, false));
            return { month, val: (ownRate ?? null) as any, type: rateType, percent: percents?.[index] };
        }

        const pricedFromResponse = getSellRateByOfferType(state, offerType, month) || undefined;
        const sellRate = isDuplicated
            ? ownOverride?.sellRate ??
              (isDealsColumnOnSharedTerm ? getSellRateOverrideByOfferTypeAndTerm(state, offerType, month, false) : undefined) ??
              (ownPricedCell?.sellRate as number | undefined) ??
              pricedFromResponse ??
              null
            : ownOverride?.sellRate ?? getSellRateOverrideByOfferTypeAndTerm(state, offerType, month, true, true);

        return { month, val: sellRate!, type: rateType, percent: percents?.[index] };
    });
};

export const getPaymentGridSellRates = (state: OfferReduxBaseState): SellRateItem[] => {
    const terms = getPaymentGridTerms(state);
    return getSellRatesFromTerms(state, terms);
};

/**
 * Residual unit is a per-column choice: each term keeps whichever unit it was overridden in, which is why the
 * save format names the unit per column (`residualDollarOverride` / `residualPercentOverride`) rather than
 * re-deriving it from the offer type. A column with no override of its own falls back to the offer type's
 * default — percent for lease, dollars for balloon.
 *
 * The selected term's column reads the deal-level override rather than its own entry in `overridesByTerm`.
 * Under user-program-quotes a deal-screen residual edit writes only the deal-level override, so a column
 * pinned to a value typed in the grid would never pick that edit up — the two would disagree from the second
 * edit onwards.
 *
 * Unlike the sell rate path this does not fall back to 0: a term column with no residual stays null so the
 * cell renders blank instead of showing a 0 that reads as a real lender value.
 */
const getResidualsFromTerms = (
    state: OfferReduxBaseState,
    terms: (number | null)[],
    residualTypeByColumn?: Record<number, ResidualValueType>,
    openingResidualType?: ResidualValueType
): ResidualsFromTerms => {
    const offerType = getCurrentOfferType(state);
    const selectedTermMonths = getSelectedTermMonths(state);
    // Called with one argument, as the rate row does, so `createSelector`'s single-entry cache is not thrashed.
    const columnView = getPaymentGridColumnView(state);
    const decidedColumns = getPaymentGridDecidedColumns(state, offerType);
    const dealsOwnColumn = getResolvedPaymentGridSelectedColumnIndex(state, offerType);
    const dealsColumn = isAspPaymentGridRedesignActive(state, offerType)
        ? getPaymentGridTermOwnerColumnIndex(state, offerType)
        : undefined;
    const splitOffTerms = (getOfferDetails(state, offerType).paymentGridData as GridPaymentGridData)?.splitOffTerms;
    const dealResidualType = getFeatureToggle(state, EXPLICIT_LENDER_P1_TOGGLE)
        ? getResidualInfoOverride(state, true).baseResidualType
        : undefined;

    return terms.map((month, index): ResidualItem => {
        if (!month) {
            return { month, val: null };
        }
        const ownResidualInfo = getOwnColumnOverride(state, offerType, index, month)?.residualInfo;
        const isDuplicated =
            isTermDuplicated(terms, month) ||
            isTermOverrideOwnedByAnotherColumn(state, offerType, terms, index, month) ||
            (ownResidualInfo == null && wasGridWritten(state, offerType, month, 'residual'));
        // Same rule the rate row uses (`getSellRatesFromTerms`): a column this build has not quoted shows only
        // what it owns, never the lender figure sitting in `offer.terms` from an earlier program-quotes build.
        // That is DE762937 issue 4 — a stale residual above an empty payment cell on a fresh open with Auto
        // Select already off — and `quotedTerms` answers it where the deal's calculation endpoint could not:
        // unticking Auto Select moves the endpoint, so gating on that emptied every column under the user
        // mid-session, while `quotedTerms` reflects what this build actually quoted.
        const showsOwnResidualOnly = columnView.isMasked && !isColumnQuotedInView(columnView, decidedColumns, terms, index, month);
        const isDealsColumnOnSharedTerm = index === dealsColumn && isTermDuplicated(terms, month);
        const residualInfo = isDuplicated
            ? ownResidualInfo ??
              (isDealsColumnOnSharedTerm ? getResidualInfoOverrideByOfferTypeTerm(state, offerType, month, false) : undefined)
            : ownResidualInfo ?? getResidualInfoOverrideByOfferTypeTerm(state, offerType, month, !showsOwnResidualOnly);
        // With the fallback on, the read always names a unit — percent for lease, the only offer type this row
        // renders for. An unquoted column has only its own override to go on, so it names the offer type's unit
        // itself, keeping the `%` on a blank cell and dispatching an edit in the right unit.
        const isDealsOwnColumn = month === selectedTermMonths && (dealsOwnColumn === undefined || dealsOwnColumn === index);
        const residualType =
            residualTypeByColumn?.[index] ??
            (isDealsOwnColumn ? dealResidualType : undefined) ??
            ownResidualInfo?.baseResidualType ??
            openingResidualType ??
            residualInfo?.baseResidualType ??
            RESIDUAL_PERCENT;
        const isDollar = residualType === RESIDUAL_DOLLAR;
        const valueKey = isDollar ? 'programResidual' : 'programResidualPercent';

        // A column the user has typed into holds only the unit they typed in - `setPaymentGridColumnOverride` replaces
        // `residualInfo` wholesale - so the other unit comes from what the quote answered for this column.
        const needsPairedFigure = ownResidualInfo != null && ownResidualInfo[valueKey] == null && !showsOwnResidualOnly;
        const ownPricedCell =
            (isDuplicated && !ownResidualInfo) || needsPairedFigure
                ? getOwnPricedCellForColumn(state, offerType, month, index)
                : undefined;

        const ownPricedResidual = residualFromCell(ownPricedCell, isDollar);

        if (ownResidualInfo?.[valueKey] == null && isDealsOwnColumn) {
            const selectedOverride = getProgramResidualOverrideForSelectedTerm(state, valueKey);
            // The quote wins where it answered this unit: `overridesByTerm` MERGES residual info, so the deal-level
            // figure for the unit the column is no longer in can predate the column's own edit.
            if (selectedOverride != null && !(needsPairedFigure && ownPricedResidual != null)) {
                return { month, val: selectedOverride, type: residualType };
            }
        }

        const ownQuotedResidual =
            splitOffTerms?.[month] === true &&
            !isDuplicated &&
            ownResidualInfo == null &&
            !showsOwnResidualOnly &&
            getResidualInfoOverrideByOfferTypeTerm(state, offerType, month, false)?.[valueKey] == null
                ? residualFromCell(getOwnPricedCellForColumn(state, offerType, month, index), isDollar)
                : null;

        return { month, val: ownQuotedResidual ?? residualInfo?.[valueKey] ?? ownPricedResidual ?? null, type: residualType };
    });
};

export const getPaymentGridResiduals = (
    state: OfferReduxBaseState,
    residualTypeByColumn?: Record<number, ResidualValueType>,
    openingResidualType?: ResidualValueType
): ResidualItem[] => {
    const terms = getPaymentGridTerms(state);
    return getResidualsFromTerms(state, terms, residualTypeByColumn, openingResidualType);
};

const getAdjustedResidualsFromTerms = (state: OfferReduxBaseState, terms: (number | null)[]): AdjustedResidualItem[] => {
    const offerType = getCurrentOfferType(state);
    const columnView = getPaymentGridColumnView(state);
    const decidedColumns = getPaymentGridDecidedColumns(state, offerType);

    return terms.map((month, index): AdjustedResidualItem => {
        if (!month) return { month, amount: null };

        if (columnView.isMasked && !isColumnQuotedInView(columnView, decidedColumns, terms, index, month)) {
            return { month, amount: null };
        }

        const source =
            getOwnPricedCellForColumn(state, offerType, month, index) ?? getTermByOfferTypeAndMonths(state, offerType, month);

        return { month, amount: toPricedNumberOrNull(source?.adjustedResidualValue) };
    });
};

export const getPaymentGridAdjustedResiduals = (state: OfferReduxBaseState): AdjustedResidualItem[] =>
    getAdjustedResidualsFromTerms(state, getPaymentGridTerms(state));

export const getFlexBuyPaymentGridSellRates = (state: OfferReduxBaseState): SellRatesFromTerms => {
    const terms = getFlexBuyPaymentGridTerms(state);
    const percents = getFlexBuyPaymentGridPercents(state);

    return getSellRatesFromTerms(state, terms, percents);
};

export const getPaymentGridItem = (
    state: OfferReduxBaseState,
    offerDetails: IPencilOfferInputs,
    termValue: number,
    cashDownVal: number,
    columnIndex: number,
    rowIndex: number
) =>
    createSelector(
        [
            (_state, termIdArgs) => getTermId(termIdArgs.offerType, termIdArgs.termMonths, termIdArgs.downPayment),
            (_state, termIdArgs) => isPaymentGridCellLoading(_state, termIdArgs.offerType, columnIndex, rowIndex, termValue),
            (_state, termIdArgs) => {
                const cell = getPaymentGridColumnCells(_state, termIdArgs.offerType)[columnIndex]?.[rowIndex];
                return isCellPricedFor(cell, termIdArgs.termMonths as number, termIdArgs.downPayment as number) ? cell : undefined;
            },
            (_state, termIdArgs) => getResolvedPaymentGridSelectedColumnIndex(_state, termIdArgs.offerType),
            (_state, termIdArgs) => getResolvedPaymentGridSelectedRowIndex(_state, termIdArgs.offerType),
            (_state, termIdArgs) =>
                getPaymentGridTerms(_state, termIdArgs.offerType).filter((term) => term === termIdArgs.termMonths).length > 1,
            (_state, termIdArgs) =>
                getPaymentGridCashDown(_state, termIdArgs.offerType).filter((cash) => cash === termIdArgs.downPayment).length > 1,
            (_state, termIdArgs) => isAspPaymentGridRedesignActive(_state, termIdArgs.offerType),
            (_state, termIdArgs) => getPaymentGridDecidedColumns(_state, termIdArgs.offerType)[columnIndex]
        ],
        (
            termId,
            isLoading,
            columnCell,
            selectedColumnIndex,
            selectedRowIndex,
            isTermDuplicated,
            isCashDownDuplicated,
            isRedesignActive,
            ownVerdict
        ): PaymentGridCellItem => {
            const termRecord = getTerm(state, termId!);
            const isOwnBlank = isRedesignActive && isTermDuplicated && ownVerdict === PaymentGridSource.Unavailable;
            const cell = columnCell ?? (isOwnBlank ? undefined : termRecord);
            const emptyTerm = columnCell ? columnCell.monthlyPayment == null : !cell || isEmpty(cell);
            if (!cell || emptyTerm || termValue === null || cashDownVal === null) {
                return {
                    id: termValue === 0 ? `term_0_downpayment_${cashDownVal}` : termId,
                    isEmpty: true,
                    isLoading
                };
            }

            const { monthlyPayment, lenderCode } = cell;
            const isSelectedColumn = !isTermDuplicated || selectedColumnIndex === undefined || selectedColumnIndex === columnIndex;
            const isSelectedRow = !isCashDownDuplicated || selectedRowIndex === undefined || selectedRowIndex === rowIndex;

            const hasResolvedPosition = isRedesignActive && selectedColumnIndex !== undefined && selectedRowIndex !== undefined;

            return {
                id: termId,
                monthlyPayment,
                lenderCode,
                isSelected: hasResolvedPosition
                    ? selectedColumnIndex === columnIndex && selectedRowIndex === rowIndex
                    : termId === offerDetails.term && isSelectedColumn && isSelectedRow,
                isEmpty: false,
                isLoading
            };
        }
    );

export const getPaymentGridItems = createSelector(
    [
        (state: OfferReduxBaseState) => state,
        (state: OfferReduxBaseState, offerType?: OfferType) => offerType || getCurrentOfferType(state),
        (state: OfferReduxBaseState, offerType?: OfferType) => getOfferDetails(state, offerType || getCurrentOfferType(state)),
        (state: OfferReduxBaseState, offerType?: OfferType) => getPaymentGridTerms(state, offerType),
        (state: OfferReduxBaseState, offerType?: OfferType) => getPaymentGridCashDown(state, offerType)
    ],
    (state, offerType, offerDetails: IPencilOfferInputs, terms: (number | null)[], cashDowns: (number | null)[]): any => {
        return cashDowns.map((cashDownVal, cashDownKey) => ({
            key: cashDownKey,
            val: cashDownVal,
            items: terms.map((termMonths: number | null, columnIndex: number) =>
                getPaymentGridItem(
                    state,
                    offerDetails,
                    termMonths as number,
                    cashDownVal as number,
                    columnIndex,
                    cashDownKey
                )(state, { offerType, termMonths, downPayment: cashDownVal })
            )
        }));
    }
);

export const getNewSelectedCell = createSelector(
    (state) => state,
    getCurrentOfferType,
    getOfferDetails,
    getSelectedTermMonths,
    getDownPayment,
    getPaymentGridCashDown,
    getPaymentGridTerms,
    (
        state,
        offerType: OfferType,
        offerDetails: IPencilOfferInputs,
        currentTermMonth: number,
        currentDownPayment: number,
        cashDown: number[],
        terms: number[]
    ) => {
        /*NOTE: Get all paymentGridItems as linear array*/
        const flatList = cashDown.reduce<
            Array<{
                id: string | undefined;
                isEmpty: boolean;
                isSelected: boolean;
                cashDownVal: number;
                cashDownIdx: number;
                termValue: number;
                termIdx: number;
            }>
        >((list, cashDownVal, cashDownIdx) => {
            const items = terms.map((termValue, termIdx) => {
                const termId = getTermId(offerType, termValue, cashDownVal);
                const termRecord = getTerm(state, termId);
                const emptyTerm = !termRecord || isEmpty(termRecord);

                return {
                    id: termId,
                    isEmpty: emptyTerm || termValue === null || cashDownVal === null,
                    isSelected: termId === offerDetails.term,
                    cashDownVal,
                    cashDownIdx,
                    termValue,
                    termIdx
                };
            });
            return [...list, ...items];
        }, []);

        const selected = flatList.filter(({ isSelected, isEmpty }) => isSelected && !isEmpty);
        if (selected.length > 0) return null;

        const findNewIndex = (cells: any, indexProperty: any) => {
            const indexList = cells.map((item: any) => item[indexProperty]);
            const [selectedIdx] = cells
                .filter(({ isSelected }: { isSelected: boolean }) => isSelected)
                .map((item: any) => item[indexProperty]);
            const newIdx = [0, 1].includes(selectedIdx) ? Math.min(...indexList) : Math.max(...indexList);
            return cells.find((item: any) => item[indexProperty] === newIdx);
        };

        const row = flatList.filter((item) => item.cashDownVal === currentDownPayment);
        const rowCells = row.filter(({ isEmpty }) => !isEmpty);

        /*NOTE: Find the nearest termIdx */
        if (rowCells.length > 0) {
            return findNewIndex(rowCells, 'termIdx');
        }

        const column = flatList.filter((item) => item.termValue === currentTermMonth);
        const columnCells = column.filter(({ isEmpty }) => !isEmpty);

        if (columnCells.length > 0) {
            /*NOTE: Find the nearest cashDownIdx */
            return findNewIndex(columnCells, 'cashDownIdx');
        }

        /*NOTE: If logic can't find selected row, it will return first one as selected*/
        return flatList.find(({ isEmpty }) => !isEmpty);
    }
);

export const getStandardGridValues = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    offerTermId: string,
    termMonths: number,
    downPayment: number
): PdfTermGridItem | undefined => {
    const termId = getTermId(offerType, termMonths, downPayment);
    const termRecord = getTerm(state, termId);

    if (hasUndefinedValue([termRecord, termMonths, downPayment]) || !termRecord || isEmpty(termRecord)) {
        return;
    }

    const {
        months,
        sellRate,
        monthlyPayment,
        rateType,
        baseResidualDollar: balloonAmount,
        totalOfPaymentsAndConsideration
    } = termRecord;

    return {
        term: months,
        sellRate,
        downPayment,
        monthlyPayment,
        balloonAmount,
        isSelected: termId === offerTermId,
        rateType,
        ...(totalOfPaymentsAndConsideration != null && { totalOfPaymentsAndConsideration })
    };
};

export const getFfbGridValues = (state: any, offerTermId: string, termMonths: number, downPayment: number, percent: number) => {
    const termId = getAutoTermId(state, termMonths, {
        offerType: FINANCE,
        extension: { DOWNPAYMENT: downPayment, FORDFLEXBUY: percent }
    });
    const termRecord = getTerm(state, termId);

    if (hasUndefinedValue([termRecord, termMonths, downPayment]) || isEmpty(termRecord)) {
        return;
    }

    const {
        months,
        initialPaymentDiscount,
        averagePaymentAmount,
        firstTermPaymentDetail,
        secondTermPaymentDetail,
        comparableTermPaymentDetail
    } = termRecord;
    return {
        term: months,
        downPayment,
        initialPaymentDiscount,
        avgMonthlyPayment: averagePaymentAmount,
        firstTermPaymentDetail,
        secondTermPaymentDetail,
        comparableTermPaymentDetail,
        isSelected: termId === offerTermId
    };
};

export const getPaymentGridABPencilPrinting = createSelector(
    [(state) => state, getCurrentOfferType, getOfferDetails, getPaymentGridTerms, getPaymentGridCashDown, getSpecialPaymentFormula],
    (
        state,
        offerType: OfferType,
        offerDetails: IPencilOfferInputs,
        termsOptions: (number | null)[],
        downPaymentOptions: (number | null)[],
        paymentFormula: SpecialPaymentFormulaType | undefined
    ): PDfPaymentGrid => {
        const ffbType = paymentFormula === SpecialPaymentFormula.FordFlexBuy;
        const offerTermId = offerDetails.term;
        const downPaymentLabel = offerType === FINANCE ? 'Cash Down' : 'Due at Signing';

        const gridData = ffbType
            ? getFlexBuyPaymentGridItems(state)
            : downPaymentOptions.map((downPayment) =>
                  termsOptions.map((termMonths) =>
                      getStandardGridValues(state, offerType, offerTermId as string, termMonths as number, downPayment as number)
                  )
              );

        return {
            gridData,
            termsOptions: termsOptions as number[],
            downPaymentLabel,
            downPaymentOptions: downPaymentOptions as number[]
        };
    }
);
